(() => {
    const settings = window.gwpSettings || {};
    const giftVariantId = Number(settings.variantId);
    const threshold = Number(settings.threshold);
    const thresholdActive = String(settings.thresholdRaw ?? '').trim() !== '' && !Number.isNaN(threshold) && threshold > 0;
    const requiredProductIds = (Array.isArray(settings.requiredProductIds) ? settings.requiredProductIds : [])
        .map(Number)
        .filter((id) => !Number.isNaN(id) && id > 0);
    const productConditionActive = requiredProductIds.length > 0;
    const matchMode = settings.matchMode === 'all' ? 'all' : 'any';
    const discountCode = String(settings.discountCode || '').trim();
    const trackingLabel = String(settings.trackingLabel || '').trim();

    // Require: feature on, a gift configured, and at least one active trigger condition.
    if (!settings.enabled || !giftVariantId) return;
    if (!thresholdActive && !productConditionActive) return;

    const automaticGiftProperty = '_automatic_gwp';
    const trackingAttribute = 'gwp_promo';
    const cartMutationPaths = [
        window.routes?.cart_add_url,
        window.routes?.cart_change_url,
        window.routes?.cart_update_url,
        '/cart/add',
        '/cart/add.js',
        '/cart/change',
        '/cart/change.js',
        '/cart/update',
        '/cart/update.js',
        '/cart/clear',
        '/cart/clear.js',
    ].filter(Boolean);
    let isSyncing = false;
    let shouldSyncAgain = false;
    const requestTimeout = 10000;

    const fetchWithTimeout = (resource, options = {}) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

        return fetch(resource, {
            ...options,
            signal: controller.signal,
        }).finally(() => clearTimeout(timeoutId));
    };

    const getPathname = (resource) => {
        try {
            const url = typeof resource === 'string' ? resource : resource.url;
            return new URL(url, window.location.origin).pathname;
        } catch (error) {
            console.error('Error parsing cart request URL for GWP:', error);
            return '';
        }
    };

    const isCartMutation = (resource) => {
        const pathname = getPathname(resource);

        return cartMutationPaths.some((path) => pathname === path || pathname.endsWith(path));
    };

    const requestCart = () => fetchWithTimeout('/cart.js', {
        headers: {
            Accept: 'application/json',
        },
    }).then((response) => response.json());

    const isConfiguredGiftLine = (item) => item.variant_id === giftVariantId;

    const getEligibleTotal = (cart) => {
        const configuredGiftTotal = cart.items
            .filter(isConfiguredGiftLine)
            .reduce((total, item) => total + item.final_line_price, 0);

        return cart.total_price - configuredGiftTotal;
    };

    // Evaluates the configured trigger conditions against the current cart.
    // Only conditions that are filled in by the merchant are evaluated; the
    // active ones are combined with AND (matchMode 'all') or OR (matchMode 'any').
    const isCartEligible = (cart) => {
        const conditions = [];

        if (thresholdActive) {
            const eligibleTotal = getEligibleTotal(cart);
            conditions.push(eligibleTotal > 0 && eligibleTotal >= threshold);
        }

        if (productConditionActive) {
            const productIdsInCart = new Set(
                cart.items
                    .filter((item) => item.variant_id !== giftVariantId)
                    .map((item) => item.product_id)
            );
            conditions.push(requiredProductIds.every((id) => productIdsInCart.has(id)));
        }

        if (!conditions.length) return false;

        return matchMode === 'all'
            ? conditions.every(Boolean)
            : conditions.some(Boolean);
    };

    const publishCartUpdate = (cart) => {
        if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'automatic-gwp',
            cartData: cart,
            variantId: giftVariantId,
        });
    };

    const refreshCartRecommendations = () => {
        document.dispatchEvent(new CustomEvent('cart:refresh', {
            detail: {
                source: 'automatic-gwp',
            },
        }));
    };

    const setCartLoading = (isLoading) => {
        const cartContainers = [
            document.getElementById('CartDrawer-CartItems'),
            document.getElementById('main-cart-items'),
            document.querySelector('cart-drawer-items'),
            document.querySelector('cart-items'),
        ].filter(Boolean);
        const statusElement = document.getElementById('CartDrawer-LineItemStatus') ||
            document.getElementById('shopping-cart-line-item-status');

        cartContainers.forEach((container) => {
            container.classList.toggle('cart__items--disabled', isLoading);
        });

        if (statusElement) {
            statusElement.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
        }

        if (isLoading) {
            document.querySelectorAll('#CartDrawer-CartItems .loading__spinner, #main-cart-items .loading__spinner')
                .forEach((spinner) => {
                    if (spinner.classList.contains('hidden')) {
                        spinner.dataset.automaticGwpLoading = 'true';
                        spinner.classList.remove('hidden');
                    }
                });
            return;
        }

        document.querySelectorAll('[data-automatic-gwp-loading="true"]').forEach((spinner) => {
            spinner.classList.add('hidden');
            delete spinner.dataset.automaticGwpLoading;
        });
    };

    const hasDiscountCode = (cart) => {
        if (!discountCode) return true;

        const normalizedDiscountCode = discountCode.toLowerCase();
        const discountApplications = [
            ...(cart.cart_level_discount_applications || []),
            ...(cart.items || []).flatMap((item) => item.discount_allocations || []),
        ];

        return discountApplications.some((discount) => {
            const title = String(
                discount.title ||
                discount.code ||
                discount.discount_application?.title ||
                discount.discount_application?.code ||
                ''
            ).toLowerCase();
            return title === normalizedDiscountCode;
        });
    };

    const addGift = () => fetchWithTimeout('/cart/add.js', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: JSON.stringify({
            id: giftVariantId,
            quantity: 1,
            properties: {
                // Marks the line as auto-added and carries the campaign tracking
                // label (falls back to 'true') so GWP orders/units are reportable.
                [automaticGiftProperty]: trackingLabel || 'true',
            },
        }),
    });

    // Stamps a cart attribute so the order is taggable/reportable in Shopify even
    // if the gift line item properties are not surfaced in a given report.
    const setTrackingAttribute = () => {
        if (!trackingLabel) return Promise.resolve();

        return fetchWithTimeout('/cart/update.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({
                attributes: {
                    [trackingAttribute]: trackingLabel,
                },
            }),
        }).catch((error) => {
            console.error('Error setting GWP tracking attribute:', error);
        });
    };

    const applyDiscountCode = () => {
        return fetchWithTimeout(`/discount/${encodeURIComponent(discountCode)}?redirect=/cart`, {
            credentials: 'same-origin',
        });
    };

    const syncGift = async () => {
        if (isSyncing) {
            shouldSyncAgain = true;
            return;
        }

        isSyncing = true;
        let didSetCartLoading = false;

        try {
            const cart = await requestCart();
            const hasGiftProduct = cart.items.some((item) => item.variant_id === giftVariantId);
            const isEligible = isCartEligible(cart);
            let cartChanged = false;
            let shouldEnsureDiscount = false;
            let cartToPublish = null;

            // Add-only: never auto-removes the gift if the cart later drops below
            // the trigger, to avoid disrupting the shopper's cart flow.
            if (isEligible && !hasGiftProduct) {
                setCartLoading(true);
                didSetCartLoading = true;

                const response = await addGift();
                cartChanged = response.ok;
                shouldEnsureDiscount = response.ok;

                if (response.ok) {
                    await setTrackingAttribute();
                }
            }

            if (shouldEnsureDiscount && discountCode) {
                const currentCart = cartChanged ? await requestCart() : cart;

                if (!hasDiscountCode(currentCart)) {
                    await applyDiscountCode();
                    const discountedCart = await requestCart();

                    if (hasDiscountCode(discountedCart)) {
                        cartToPublish = discountedCart;
                    } else {
                        console.error('GWP discount code was not applied to the cart.');
                        cartToPublish = discountedCart;
                    }
                } else if (cartChanged) {
                    cartToPublish = currentCart;
                }

                cartChanged = true;
            }

            if (cartChanged) {
                publishCartUpdate(cartToPublish || await requestCart());
                refreshCartRecommendations();
            }

        } catch (error) {
            console.error('Error syncing automatic gift with purchase:', error);
        } finally {
            if (didSetCartLoading) {
                setCartLoading(false);
            }
            isSyncing = false;

            if (shouldSyncAgain) {
                shouldSyncAgain = false;
                syncGift();
            }
        }
    };

    const originalFetch = window.fetch;

    window.fetch = function (...args) {
        const response = originalFetch.apply(this, args);

        if (isCartMutation(args[0])) {
            response.then(() => {
                if (isSyncing) {
                    shouldSyncAgain = true;
                    return;
                }

                syncGift();
            }).catch(() => {});
        }

        return response;
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', syncGift, { once: true });
    } else {
        syncGift();
    }
})();
